import {
  ArrowDownTrayIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import date from "date-and-time";
import { IInvoice } from "../../models/invoice/IInvoice";
import useInvoices from "../../data/useInvoices";
import { useEffect, useMemo, useState } from "react";
import InvoiceModal from "../invoices/InvoiceModal";
import { useForm } from "react-hook-form";
import SelectInput from "../shared/Inputs/SelectInput";
import { InvoiceStatus } from "../../models/Invoice";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import { SearchAccount } from "../accounts/SearchAccount";
import PagedTable, { DateRangeType } from "../shared/Tables/PagedTable";
import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";
import IndeterminateCheckbox from "../shared/Tables/IndeterminateCheckbox";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import useApiHelper from "../../hooks/useApiHelper";
import apiConfig from "../../utils/apiConfig";
import useInvoiceMutations from "../../data/useInvoiceMutations";
import useNotification from "../notifications/useNotifications";

interface InvoiceRowProps {
  invoice: IInvoice;
  onClick: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
}

interface InvoiceListProps {
  status?: string;
  accountId?: string;
  partId?: string;
  invoiceDate?: DateRangeType;
  hideFilters?: boolean;
}

function DownloadInvoiceButton({
  invoiceId,
  download,
  onError,
}: {
  invoiceId: string;
  download: (id: string) => Promise<void>;
  onError: () => void;
}) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloading(true);
    download(invoiceId)
      .catch(onError)
      .finally(() => setDownloading(false));
  };

  return (
    <div className="px-3 flex flex-col items-center justify-center">
      {downloading ? (
        <svg
          role="status"
          className="w-5 h-5 text-gray-300 animate-spin fill-primary-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
      ) : (
        <ArrowDownTrayIcon
          className="w-5 h-5 text-gray-600 hover:text-primary transition-colors duration-200 cursor-pointer"
          onClick={handleClick}
        />
      )}
    </div>
  );
}

export default function InvoiceList(filters: InvoiceListProps) {
  const { handleSubmit, register, setValue, watch } = useForm({
    defaultValues: {
      status: filters.status ?? undefined,
      accountId: filters.accountId ?? undefined,
      partId: filters.partId ?? undefined,
      invoiceDate: filters.invoiceDate ?? { fieldName: "invoiceDate", start: null, end: null },
    }
  });
  const { post } = useApiHelper(apiConfig("InvoiceList"));

  const status = watch("status");
  const accountId = watch("accountId");
  const partId = watch("partId");
  const start = watch("invoiceDate.start");
  const end = watch("invoiceDate.end");
  const dateRange = watch("invoiceDate") as DateRangeType;


  const trueFilters = useMemo(() => ({
    status: filters.status ?? status,
    accountId: filters.accountId ?? accountId,
    partId: filters.partId ?? partId,
    invoiceDate: filters.invoiceDate ?? (start && end ? dateRange : undefined),
  }), [filters, status, accountId, partId, start, end, dateRange]);


  const { downloadInvoice, downloadBulkInvoices } = useInvoiceMutations();
  const { addNotification } = useNotification();

  const notifyDownloadError = () =>
    addNotification({
      variant: "error",
      primaryText: "Failed to download invoice",
    });

  const [bulkDownloading, setBulkDownloading] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [invoice, setInvoice] = useState<IInvoice | null>(null);

  const colHelper = createColumnHelper<IInvoice>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(false),
      colHelper.accessor("invoiceNumber", {
        header: "Invoice No",
        sortingFn: "auto",
      }),
      colHelper.accessor("account.name", {
        header: "Account Name",
        sortingFn: "auto",
      }),
      colHelper.accessor("invoiceDate", {
        header: "Invoice Date",
        sortingFn: "auto",
        cell: (info) => <span>{info.getValue() !== null ? date.format(new Date(info.getValue()!), "DD/MM/YYYY") : "Not Set"}</span>,
      }),
      colHelper.accessor("reference", {
        header: "Reference",
        sortingFn: "auto",
      }),
      colHelper.accessor("dueDate", {
        header: "Due Date",
        sortingFn: "auto",
        cell: (info) => <span>{info.getValue() !== null ? date.format(new Date(info.getValue()!), "DD/MM/YYYY") : "Not Set"}</span>,
      }),
      colHelper.accessor("invoiceTotal", {
        header: "Total",
        sortingFn: "auto",
        cell: (info) => <span>£{info.getValue() !== null ? info.getValue() : "Not Set"}</span>,
      }),
      colHelper.accessor("status", {
        header: "Status",
        sortingFn: "auto",
        cell: (info) => <span
          className={`px-3 py-1 rounded-full font-bold ${{
            0: "bg-blue-100 text-blue-600",
            1: "bg-amber-100 text-amber-600",
            2: "bg-green-100 text-green-600",
            3: "bg-red-100 text-red-600",
          }[info.getValue()]
            }`}
        >
          {
            {
              0: "Draft",
              1: "Approved",
              2: "Paid",
              3: "Credit",
            }[info.getValue()]
          }
        </span>,
      }),
      colHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <DownloadInvoiceButton
            invoiceId={info.row.original.id}
            download={downloadInvoice}
            onError={notifyDownloadError}
          />
        ),
      }),
    ],
    []
  );

  const onSubmit = (data: any) => {
    console.log(data);
  };

  useEffect(() => {
    if (openModal) return;
    setInvoice(null);

  }, [openModal])

  // var table = undefined;

  // if (invoices.isLoading) {
  //   table = (
  //     <div className="flex items-center justify-center py-10">
  //       <LoadingWheel />
  //     </div>
  //   );
  // } else if (
  //   invoices.isError ||
  //   !invoices.data ||
  //   !invoices.data.data
  // ) {
  //   table = (
  //     <div className="flex items-center justify-center py-10">
  //       <p className="text-red-600 font-bold text-xl">Error loading invoices</p>
  //     </div>
  //   );
  // } else {
  //   table = (

  //   );
  // }


  return <>
    {/* ---------- Filter ---------- */}
    <form onSubmit={handleSubmit(onSubmit)} className="mt-2 mx-4 grid grid-cols-3 gap-4">
      {filters.accountId == undefined && (
        <div className="col-span-3">
          <SearchAccount
            label="Account"
            onAccountSelect={(account) => setValue("accountId", account?.id)}
          />
        </div>
      )}

      {filters.status == undefined && (
        <SelectInput
          label="Status"
          options={[
            { id: undefined, name: "All" },
            { id: InvoiceStatus.Draft, name: "Draft" },
            { id: InvoiceStatus.Approved, name: "Approved" },
            { id: InvoiceStatus.Paid, name: "Paid" },
            { id: InvoiceStatus.Credit, name: "Credit" },
          ]}
          register={register}
          registerName="status"
          setValue={setValue}
          watch={watch}
        />
      )}
      {filters.invoiceDate?.start == undefined && (
        <DateTimeInput
          label="From"
          register={register}
          registerName="invoiceDate.start"
        />
      )}
      {filters.invoiceDate?.end == undefined && (
        <DateTimeInput
          label="To"
          register={register}
          registerName="invoiceDate.end"
        />
      )}

    </form>
    <hr className="text-gray-300 mt-8 col-span-3 mx-4" />
    {/* ---------------------------- */}
    <div className="p-4">
      <InvoiceModal
        open={openModal}
        setOpen={setOpenModal}
        invoice={invoice}
      />

      <div className="sm:hidden p-2">
        <p className="text-center">Tap a row to see the full record</p>
      </div>

      <PagedTable
        url="/invoice/filtered"
        columns={cols}
        queryLabel="Search part details"
        onRowClick={(x: IInvoice) => {
          setInvoice(x);
          setOpenModal(true);
        }}
        showTotal={true}
        additionalBody={trueFilters}
        actionButtons={[
          {
            label: "Download Xero",
            onClick: (rows: RowModel<IInvoice>, table: Table<IInvoice>) => {
              setBulkDownloading(true);
              downloadBulkInvoices(rows.rows.map((row: any) => row.original.id), "xero")
                .catch(() =>
                  addNotification({
                    variant: "error",
                    primaryText: "Failed to download invoices",
                  })
                )
                .finally(() => setBulkDownloading(false));
            },
            disabledOnNoRows: true,
            isLoading: bulkDownloading,
          }
        ]}
      />
    </div>
  </>
}
